import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const ProjectContext = createContext();
const TABLE_NAME = 'projects_1740000000000';

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('cf_dark_mode') === 'true';
  });

  useEffect(() => {
    fetchProjects();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('schema-db-changes')
      .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: TABLE_NAME 
        }, 
        () => {
          fetchProjects();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('cf_dark_mode', darkMode);
  }, [darkMode]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const addProject = async (project) => {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .insert([project]);
      if (error) throw error;
    } catch (error) {
      console.error('Error adding project:', error);
      alert('Failed to add project');
    }
  };

  const updateProject = async (id, updatedFields) => {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update(updatedFields)
        .eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    }
  };

  const signOffProject = async (id, signerName) => {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({
          signed_off: true,
          signed_off_by: signerName,
          signed_off_date: new Date().toISOString(),
          status: 'Complete'
        })
        .eq('id', id);
      if (error) throw error;
      await fetchProjects();
    } catch (error) {
      console.error('Error signing off project:', error);
    }
  };

  const archiveProject = async (id) => {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ 
          archived_at: new Date().toISOString(),
          status: 'Archived' 
        })
        .eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error('Error archiving project:', error);
    }
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from(TABLE_NAME)
          .delete()
          .eq('id', id);
        if (error) throw error;
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <ProjectContext.Provider value={{
      projects,
      loading,
      addProject,
      updateProject,
      signOffProject,
      archiveProject,
      deleteProject,
      darkMode,
      toggleDarkMode,
      refreshProjects: fetchProjects
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => useContext(ProjectContext);